import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Rocket, ExternalLink, RefreshCw } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function AdminPublishing() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const r = await api.get("/admin/publishing/queue");
            setData(r.data);
        } catch (e) { toast.error(errMsg(e, "Failed")); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    return (
        <AdminShell>
            <div className="mb-6 flex items-center justify-between" data-testid="admin-publishing-header">
                <div>
                    <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight">Publishing</h1>
                    <p className="text-[#64748B] mt-1 text-[14px]">Doctors ready to go live + those most recently published.</p>
                </div>
                <button onClick={load} disabled={loading} className="ddi-btn-outline">
                    <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-[#E5E9F2] ddi-shadow-sm" data-testid="publish-ready-queue">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-[15px] flex items-center gap-2">
                            <Rocket size={14} className="text-[#3A80F7]" /> Ready to publish
                            <span className="ddi-chip ddi-chip-primary ml-1">{data?.ready.length || 0}</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {!data || data.ready.length === 0 ? (
                            <div className="py-6 text-[13px] text-[#64748B] text-center">No doctors are waiting.</div>
                        ) : (
                            <ul className="divide-y divide-[#EEF1F6]">
                                {data.ready.map((d) => (
                                    <li key={d.id} className="py-3 flex items-center gap-3" data-testid={`ready-${d.id}`}>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-semibold text-[#0F172A] truncate">{d.full_name}</div>
                                            <div className="text-[11px] text-[#64748B]">{d.specialization} · {d.package_code}</div>
                                        </div>
                                        <Link to={`/admin/review/${d.id}`} className="text-[12px] text-[#3A80F7] font-semibold hover:underline">Review</Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardContent>
                </Card>

                <Card className="border-[#E5E9F2] ddi-shadow-sm" data-testid="publish-recent-list">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-[15px]">Recently published</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {!data || data.recently_published.length === 0 ? (
                            <div className="py-6 text-[13px] text-[#64748B] text-center">No published doctors yet.</div>
                        ) : (
                            <ul className="divide-y divide-[#EEF1F6] max-h-[500px] overflow-y-auto">
                                {data.recently_published.map((d) => (
                                    <li key={d.id} className="py-3 flex items-center gap-3" data-testid={`published-${d.id}`}>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-semibold text-[#0F172A] truncate">{d.full_name}</div>
                                            <div className="text-[11px] text-[#94A3B8]">
                                                <span className="font-mono">/dr/{d.slug}</span> · {d.published_at ? new Date(d.published_at).toLocaleDateString() : ""}
                                            </div>
                                        </div>
                                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/dr/${d.slug}`} target="_blank" rel="noopener" className="text-[12px] text-[#3A80F7] hover:underline inline-flex items-center gap-1">
                                            View <ExternalLink size={11} />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardContent>
                </Card>
            </div>
        </AdminShell>
    );
}
